import { useFetch } from '@/hooks/useFetch'
import { showError } from '@/shared/toast'
import { Prefecture } from '@/types/prefecture'
import { css, Interpolation, Theme } from '@emotion/react'
import { useEffect, useState } from 'react'
import Select, { ActionMeta, SingleValue } from 'react-select'

type Props = {
  initialValue?: string
  isSearchable?: boolean
  isClearable?: boolean
  isError?: boolean
  addStyle?: Interpolation<Theme>
  customStyles?: any
  onChange?: (
    newValue: SingleValue<{ label: string; value: string }>,
    actionMeta: ActionMeta<{ label: string; value: string }>,
  ) => void
}

const style = css`
  width: 25rem;
`

export const PrefectureSelect = ({
  onChange,
  isSearchable,
  isClearable,
  isError = false,
  initialValue,
  addStyle,
  customStyles,
}: Props) => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>([])
  const [value, setValue] =
    useState<SingleValue<{ label: string; value: string }>>(null)
  const { data, error }: any = useFetch<Prefecture[]>(
    `/api/get_prefectures_list`,
  )

  useEffect(() => {
    if (data && data.length > 0) {
      const newOptions = data.map((type: Prefecture) => ({
        label: type.name,
        value: type.prefectureCode,
      }))
      setOptions(newOptions)

      const initialOption = newOptions.find(
        (opt: any) => opt.value === initialValue,
      )
      setValue(initialOption || null)
    }
  }, [data, initialValue])

  useEffect(() => {
    if (error) {
      showError('都道府県の取得に失敗しました。')
    }
  }, [error])

  const handleChange = (
    newValue: SingleValue<{ label: string; value: string }>,
    actionMeta: ActionMeta<{ label: string; value: string }>,
  ) => {
    setValue(newValue)
    onChange?.(newValue, actionMeta)
  }

  const ERROR_COLOR = '#FF0000'

  const errorStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: isError ? ERROR_COLOR : provided.borderColor,
      '&:hover': {
        borderColor: isError ? ERROR_COLOR : provided.borderColor,
      },
    }),
  }

  const mergedStyles = customStyles
    ? {
        ...customStyles,
        control: (provided: any, state: any) => ({
          ...(customStyles.control
            ? customStyles.control(provided, state)
            : provided),
          ...(isError && {
            borderColor: ERROR_COLOR,
            '&:hover': {
              borderColor: ERROR_COLOR,
            },
          }),
        }),
      }
    : errorStyles

  return (
    <Select
      css={[style, addStyle]}
      isSearchable={isSearchable}
      isClearable={isClearable}
      value={value}
      placeholder="都道府県を選択"
      name="prefectureCode"
      options={options}
      onChange={handleChange}
      styles={mergedStyles}
    />
  )
}
